import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined"
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined"
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined"
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined"
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined"
import PermContactCalendarOutlinedIcon from "@mui/icons-material/PermContactCalendarOutlined"
import PersonOutlineIcon from "@mui/icons-material/PersonOutline"
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined"
import ShowChartIcon from "@mui/icons-material/ShowChart"
import SMSOutlinedIcon from "@mui/icons-material/SmsOutlined"
import Box from "@mui/material/Box"
import Tab from "@mui/material/Tab"
import Tabs from "@mui/material/Tabs"
import { styled } from "@mui/material/styles"
import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../../common/axios"
import Column from "../../../../common/components/Column"
import TabContent from "../../../../common/components/TabContent"
import { useAutoNavigateSearchParamState } from "../../../../common/utils"
import { MAX_PAGE_HEIGHT } from "../../utils"
import { GLOBAL_SEARCH_URL_PARAMS } from "../data"
import ActivityTimelineTab from "./activity-tabs/ActivityTimelineTab"
import CallsTab from "./activity-tabs/CallsTab"
import EmailsTab from "./activity-tabs/EmailsTab"
import FilesTab from "./activity-tabs/FilesTab"
import MeetingScheduleTab from "./activity-tabs/MeetingScheduleTab"
import NotesTab from "./activity-tabs/NotesTab"
import SMSTab from "./activity-tabs/SMSTab"
import TasksTab from "./activity-tabs/TasksTab"
import GeoInsightsTab, {
  useGeoInsightsTabState,
} from "./profile-tabs/GeoInsightsTab"
import MarketActivityTab, {
  useMarketActivityTabState,
} from "./profile-tabs/MarketActivityTab"
import SalesPerformanceTab, {
  useSalesPerformanceTabState,
} from "./profile-tabs/SalesPerformanceTab"

const MEETINGS_POLL_INTERVAL_MS = 15000

export default function ActivityTimelineColumn({
  candidate,
  setCandidate,
  isLoading,
  readOnly = false,
  onEmailButtonClicked,
  onSendSMSButtonClicked,
  onMakeCallButtonClicked,
  tasks,
  setTasks,
  onAddTaskButtonClicked,
  onTaskClicked,
  onCRMEventsChanged,
  onBookingRescheduled,
  onMeetingChanged,
}) {
  const [superTabIndex, setSuperTabIndex] = useAutoNavigateSearchParamState(
    "st",
    SUPER_TABS.activity
  )
  const [activityTabIndex, setActivityTabIndex] =
    useAutoNavigateSearchParamState("at", ACTIVITY_TABS.activity)
  const [profileTabIndex, setProfileTabIndex] = useAutoNavigateSearchParamState(
    "pt",
    PROFILE_TABS.salesPerformance
  )
  const [meetings, setMeetings] = useState([])
  const [isLoadingMeetings, setIsLoadingMeetings] = useState(false)
  const {
    selectedTimePeriod,
    setSelectedTimePeriod,
    salesVolume,
    isLoadingSalesVolume,
    averageSales,
    isLoadingAverageSales,
    closedToListPriceRatio,
    isLoadingClosedToListPriceRatio,
    averageDaysOnMarket,
    isLoadingAverageDaysOnMarket,
    unitsClosed,
    isLoadingUnitsClosed,
    rentals,
    isLoadingRentals,
    currentActivity,
    isLoadingCurrentActivity,
    salesVolumeChartData,
    isLoadingSalesVolumeChart,
    salesVolumeSidesChartData,
    isLoadingSalesVolumeSidesChartData,
  } = useSalesPerformanceTabState(candidate.ssot_agent_id)
  const {
    marketHistoryTransactions,
    isLoadingMarketHistoryTransactions,
    marketHistoryPageIndex,
    setMarketHistoryPageIndex,
    marketHistoryPageSize,
    setMarketHistoryPageSize,
    marketHistoryTotalResultsSize,
    marketHistorySortColumn,
    marketHistorySortDirection,
    onMarketHistorySortColumnChanged,
  } = useMarketActivityTabState(candidate.ssot_agent_id)
  const {
    mapTransactions,
    isLoadingMapTransactions,
    mapRelatedOffices,
    isLoadingMapRelatedOffices,
  } = useGeoInsightsTabState(candidate.ssot_agent_id)
  const axios = useAuthorizedAxiosClient()
  const searchParams = useSearchParams()[0]
  const crmEvents = candidate.crm_events

  useEffect(
    () => {
      if (isLoading || !candidate.id) {
        return
      }

      let isActive = true
      const loadMeetings = (showLoading = false) => {
        if (showLoading) {
          setIsLoadingMeetings(true)
        }
        return axios
          .get(`/crm/api/meeting-schedule/${candidate.id}/`)
          .then(({ data }) => {
            if (isActive) {
              setMeetings(data)
            }
          })
          .finally(() => {
            if (showLoading && isActive) {
              setIsLoadingMeetings(false)
            }
          })
      }

      loadMeetings(true)
      const intervalId = window.setInterval(
        loadMeetings,
        MEETINGS_POLL_INTERVAL_MS
      )

      return () => {
        isActive = false
        window.clearInterval(intervalId)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [candidate.id, isLoading]
  )

  useEffect(
    () => {
      const tabTargets = {
        [GLOBAL_SEARCH_URL_PARAMS.emailTransaction]: ACTIVITY_TABS.email,
        [GLOBAL_SEARCH_URL_PARAMS.meeting]: ACTIVITY_TABS.meetingSchedular,
        [GLOBAL_SEARCH_URL_PARAMS.call]: ACTIVITY_TABS.call,
        [GLOBAL_SEARCH_URL_PARAMS.note]: ACTIVITY_TABS.notes,
        [GLOBAL_SEARCH_URL_PARAMS.sms]: ACTIVITY_TABS.sms,
      }
      const tabTarget = Object.entries(tabTargets).find(
        ([urlParamName, tabName]) => searchParams.get(urlParamName)
      )
      if (tabTarget) {
        setSuperTabIndex(SUPER_TABS.activity)
        setActivityTabIndex(tabTarget[1])
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams]
  )

  return (
    <ContainerColumn sx={{ pr: 1 }}>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          borderRadius: "24px",
        }}
      >
        <SuperTabs
          value={superTabIndex}
          onChange={(e, newIndex) => setSuperTabIndex(newIndex)}
          variant="fullWidth"
        >
          <Tab icon={<PersonOutlineIcon />} label="Activity" />
          <Tab
            icon={<ShowChartIcon />}
            label="Performance"
            disabled={!candidate.ssot_agent_id}
          />
        </SuperTabs>
      </Box>
      <TabContent index={SUPER_TABS.activity} value={superTabIndex}>
        <StyledTabs
          value={activityTabIndex}
          onChange={(e, activeTabIndex) => setActivityTabIndex(activeTabIndex)}
        >
          <Tab label="Activity" icon={<CalendarTodayOutlinedIcon />} />
          <Tab label="Meetings" icon={<PermContactCalendarOutlinedIcon />} />
          <Tab label="Notes" icon={<DescriptionOutlinedIcon />} />
          <Tab label="Emails" icon={<EmailOutlinedIcon />} />
          <Tab label="SMS" icon={<SMSOutlinedIcon />} />
          <Tab label="Calls" icon={<PhoneOutlinedIcon />} />
          <Tab label="Tasks" icon={<AssignmentOutlinedIcon />} />
          <Tab label="Files" icon={<InsertDriveFileOutlinedIcon />} />
        </StyledTabs>
        <TabContent index={ACTIVITY_TABS.activity} value={activityTabIndex}>
          <ActivityTimelineTab
            candidate={candidate}
            setCandidate={setCandidate}
            crmEvents={crmEvents}
            meetings={meetings}
            isLoading={isLoading || isLoadingMeetings}
            readOnly={readOnly}
          />
        </TabContent>
        <TabContent
          index={ACTIVITY_TABS.meetingSchedular}
          value={activityTabIndex}
        >
          <MeetingScheduleTab
            candidate={candidate}
            meetings={meetings}
            setMeetings={setMeetings}
            isLoading={isLoadingMeetings}
            readOnly={readOnly}
            onBookingRescheduled={onBookingRescheduled}
            onMeetingChanged={onMeetingChanged}
          />
        </TabContent>
        <TabContent index={ACTIVITY_TABS.notes} value={activityTabIndex}>
          <NotesTab
            candidate={candidate}
            setCandidate={setCandidate}
            isLoading={isLoading}
            readOnly={readOnly}
          />
        </TabContent>
        <TabContent index={ACTIVITY_TABS.email} value={activityTabIndex}>
          <EmailsTab
            candidate={candidate}
            setCandidate={setCandidate}
            onEmailButtonClicked={onEmailButtonClicked}
            isLoading={isLoading}
          />
        </TabContent>
        <TabContent index={ACTIVITY_TABS.sms} value={activityTabIndex}>
          <SMSTab
            candidate={candidate}
            setCandidate={setCandidate}
            onSendSMSButtonClicked={onSendSMSButtonClicked}
            isLoading={isLoading}
            readOnly={readOnly}
          />
        </TabContent>
        <TabContent index={ACTIVITY_TABS.call} value={activityTabIndex}>
          <CallsTab
            candidate={candidate}
            onMakeCallButtonClicked={onMakeCallButtonClicked}
            isLoading={isLoading}
          />
        </TabContent>
        <TabContent index={ACTIVITY_TABS.tasks} value={activityTabIndex}>
          <TasksTab
            tasks={tasks}
            setTasks={setTasks}
            onTaskClicked={onTaskClicked}
            onAddTaskButtonClicked={onAddTaskButtonClicked}
            onCRMEventsChanged={onCRMEventsChanged}
            isLoading={isLoading}
            readOnly={readOnly}
          />
        </TabContent>
        <TabContent index={ACTIVITY_TABS.files} value={activityTabIndex}>
          <FilesTab
            candidate={candidate}
            setCandidate={setCandidate}
            isLoading={isLoading}
            readOnly={readOnly}
          />
        </TabContent>
      </TabContent>
      <TabContent index={SUPER_TABS.profile} value={superTabIndex}>
        <StyledTabs
          value={profileTabIndex}
          onChange={(e, newIndex) => setProfileTabIndex(newIndex)}
        >
          <Tab label="Sales Performance" icon={<ShowChartIcon />} />
          <Tab label="Market Activity" icon={<CalendarTodayOutlinedIcon />} />
          <Tab label="Geo Insights" icon={<DescriptionOutlinedIcon />} />
        </StyledTabs>
        <TabContent
          index={PROFILE_TABS.salesPerformance}
          value={profileTabIndex}
        >
          <SalesPerformanceTab
            candidate={candidate}
            selectedTimePeriod={selectedTimePeriod}
            setSelectedTimePeriod={setSelectedTimePeriod}
            salesVolume={salesVolume}
            isLoadingSalesVolume={isLoadingSalesVolume}
            averageSales={averageSales}
            isLoadingAverageSales={isLoadingAverageSales}
            closedToListPriceRatio={closedToListPriceRatio}
            isLoadingClosedToListPriceRatio={isLoadingClosedToListPriceRatio}
            averageDaysOnMarket={averageDaysOnMarket}
            isLoadingAverageDaysOnMarket={isLoadingAverageDaysOnMarket}
            unitsClosed={unitsClosed}
            isLoadingUnitsClosed={isLoadingUnitsClosed}
            rentals={rentals}
            isLoadingRentals={isLoadingRentals}
            currentActivity={currentActivity}
            isLoadingCurrentActivity={isLoadingCurrentActivity}
            salesVolumeChartData={salesVolumeChartData}
            isLoadingSalesVolumeChart={isLoadingSalesVolumeChart}
            salesVolumeSidesChartData={salesVolumeSidesChartData}
            isLoadingSalesVolumeSidesChartData={
              isLoadingSalesVolumeSidesChartData
            }
          />
        </TabContent>
        <TabContent index={PROFILE_TABS.marketActivity} value={profileTabIndex}>
          <MarketActivityTab
            marketHistoryTransactions={marketHistoryTransactions}
            isLoadingMarketHistoryTransactions={
              isLoadingMarketHistoryTransactions
            }
            marketHistoryPageIndex={marketHistoryPageIndex}
            setMarketHistoryPageIndex={setMarketHistoryPageIndex}
            marketHistoryPageSize={marketHistoryPageSize}
            setMarketHistoryPageSize={setMarketHistoryPageSize}
            marketHistoryTotalResultsSize={marketHistoryTotalResultsSize}
            marketHistorySortColumn={marketHistorySortColumn}
            marketHistorySortDirection={marketHistorySortDirection}
            onMarketHistorySortColumnChanged={onMarketHistorySortColumnChanged}
          />
        </TabContent>
        <TabContent index={PROFILE_TABS.geoInsights} value={profileTabIndex}>
          <GeoInsightsTab
            mapTransactions={mapTransactions}
            isLoadingMapTransactions={isLoadingMapTransactions}
            mapRelatedOffices={mapRelatedOffices}
            isLoadingMapRelatedOffices={isLoadingMapRelatedOffices}
          />
        </TabContent>
      </TabContent>
    </ContainerColumn>
  )
}

const SUPER_TABS = {
  activity: 0,
  profile: 1,
}

const ACTIVITY_TABS = {
  activity: 0,
  meetingSchedular: 1,
  notes: 2,
  email: 3,
  sms: 4,
  call: 5,
  tasks: 6,
  files: 7,
}

const PROFILE_TABS = {
  salesPerformance: 0,
  marketActivity: 1,
  geoInsights: 2,
}

const ContainerColumn = styled(Column)(({ theme }) => ({
  gap: theme.spacing(2),
  maxHeight: MAX_PAGE_HEIGHT.desktop,
  overflowY: "auto",
  position: "relative",
  [theme.breakpoints.only("xs")]: {
    maxHeight: "initial",
    overflowY: "visible",
  },
}))

const SuperTabs = styled(Tabs)(({ theme }) => ({
  borderColor: theme.palette.divider,
  borderRadius: "24px",
  background:
    "linear-gradient(90deg, rgba(255, 255, 255, 0.7) 3.01%, rgba(255, 255, 255, 0.5) 103.3%)",
  border: "1px solid rgba(255, 255, 255, 0.9)",
  boxShadow: "2px 2px 8px rgba(0, 0, 0, 0.15)",
  "& .MuiTabs-indicator": { display: "none" },
  "& .MuiTab-root": {
    minHeight: 46,
    textTransform: "none",
    fontWeight: 500,
    color: "black",
  },
  "& .MuiSvgIcon-root": {
    fill: "black",
  },
  "& .MuiTab-root.Mui-selected": {
    backgroundColor: "#dbd5f4",
    color: "black",
  },
  "& .MuiTab-root.Mui-selected .MuiSvgIcon-root": {
    fill: "black",
  },
  "& .MuiTab-root.Mui-disabled": {
    color: theme.palette.text.disabled,
  },
  "& .MuiTab-root.Mui-disabled .MuiSvgIcon-root": {
    fill: theme.palette.text.disabled,
  },
}))

const StyledTabs = styled(Tabs)(({ theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: 100,
}))
